import { Koi } from '@ksyos/koi';
import type { CustomControlCode } from '@common/enums';
import type { DependsOn } from '@common/formEngine/dependsOn';
import type { InputField, Section } from '@common/formEngine/formEngine.types';
import type { GenericData } from '@common/formEngine/generic/generic.types';
import type { DeepPickPath } from '@common/utils/deepPickUtils';
import type { NullablePrimitives } from '@common/utils/utilityTypes';
import type { Locatie } from '@common/validation/customControls/neurology/neurology';
import type { IPatientAddEditValidated } from '@common/validation/patient';
import { Gender } from '@common/enums';
import { dependsOn } from '@common/formEngine/dependsOn';
import { patient_verzekeringskosten_akkoord_field } from '@common/formEngine/generic/genericFields';
import { never } from '@common/utils/never';
import { assertIsNonNullable } from '@common/utils/nonNullableTypeUtils';
import { getObjectEntries } from '@common/utils/utilityTypes';
import { locatieOptions, thuissituatieOptions } from '@common/validation/customControls/neurology/neurology';
import { emailSchema } from '@common/validation/schemas';
import { getTodayOrBeforeDateSchema } from '@common/validation/utils/dateSchemas';
import { getTextInputStringSchema } from '@common/validation/utils/getTextInputStringSchema';

export enum AanvullendeOnderzoeken {
    anders = 'Anders',
    bloedonderzoek = 'Bloedonderzoek',
    ct_scan = 'CT-scan',
    eeg = 'EEG',
    mri = 'MRI',
    oogonderzoek = 'Oogonderzoek',
}
export const aanvullendeOnderzoekenOpties = [
    AanvullendeOnderzoeken.ct_scan,
    AanvullendeOnderzoeken.mri,
    AanvullendeOnderzoeken.eeg,
    AanvullendeOnderzoeken.oogonderzoek,
    AanvullendeOnderzoeken.bloedonderzoek,
    AanvullendeOnderzoeken.anders,
];

export enum AanvullendOnderzoekResultaat {
    afwijkend = 'Afwijkend',
    nietAfwijkend = 'Niet afwijkend',
}
export const aanvullendOnderzoekResultaatOpties = [
    AanvullendOnderzoekResultaat.nietAfwijkend,
    AanvullendOnderzoekResultaat.afwijkend,
];

export enum EerdereBehandelingen {
    anders = 'Anders',
    ergotherapie = 'Ergotherapie',
    fysio_manuele_therapie = 'Fysio- / manuele therapie',
    oefentherapie = 'Oefentherapie',
    psychologische_behandeling = 'Psychologische behandeling',
    voedingsaanpassingen_door_dietist = 'Voedingsaanpassingen door diëtist',
}
export const eerdereBehandelingenOpties = [
    EerdereBehandelingen.fysio_manuele_therapie,
    EerdereBehandelingen.oefentherapie,
    EerdereBehandelingen.psychologische_behandeling,
    EerdereBehandelingen.ergotherapie,
    EerdereBehandelingen.voedingsaanpassingen_door_dietist,
    EerdereBehandelingen.anders,
];

export enum EerdereBehandelingResultaat {
    aanzienlijkeVerbetering = 'Aanzienlijke verbetering van klachten',
    geenEffect = 'Geen effect',
    lichteVerbetering = 'Lichte verbetering van klachten',
    onduidelijk = 'Onduidelijk',
}
export const eerdereBehandelingResultaatOpties = [
    EerdereBehandelingResultaat.geenEffect,
    EerdereBehandelingResultaat.lichteVerbetering,
    EerdereBehandelingResultaat.aanzienlijkeVerbetering,
    EerdereBehandelingResultaat.onduidelijk,
];

export enum HoofdpijnKlachtenMenstruatieVanInvloed {
    jaErger = 'Ja, klachten worden erger',
    jaMinder = 'Ja, klachten worden minder',
    nee = 'Nee',
}
export const hoofdpijnKlachtenMenstruatieVanInvloedOpties = [
    HoofdpijnKlachtenMenstruatieVanInvloed.jaErger,
    HoofdpijnKlachtenMenstruatieVanInvloed.jaMinder,
    HoofdpijnKlachtenMenstruatieVanInvloed.nee,
];

export enum HoofdpijnSymptomen {
    anders = 'Anders',
    auras = `Aura's (bijv. lichtflitsen, blinde vlekken) voorafgaand aan de hoofdpijn`,
    gevoelig_voor_licht_en_geluid = 'Gevoeligheid voor licht en geluid',
    misselijkheid_en_of_braken = 'Misselijkheid en/of braken',
    tintelingen_of_andere = 'Tintelingen, gevoelloosheid, spraakproblemen of evenwichtsstoornis',
}
export const hoofdpijnSymptomenOpties = [
    HoofdpijnSymptomen.misselijkheid_en_of_braken,
    HoofdpijnSymptomen.gevoelig_voor_licht_en_geluid,
    HoofdpijnSymptomen.auras,
    HoofdpijnSymptomen.tintelingen_of_andere,
    HoofdpijnSymptomen.anders,
];

export enum HoofdpijnType {
    hoofdpijnDoorOvergebruikMedicatie = 'Hoofdpijn door overgebruik van medicatie',
    migraine = 'Migraine',
    onduidelijk = 'Onduidelijk',
    spanningshoofdpijn = 'Spanningshoofdpijn',
}
export const hoofdpijnTypeOpties = [
    HoofdpijnType.spanningshoofdpijn,
    HoofdpijnType.migraine,
    HoofdpijnType.hoofdpijnDoorOvergebruikMedicatie,
    HoofdpijnType.onduidelijk,
];

export enum MigraineInFamilie {
    ja1eGraad = 'Ja, 1e graad: ouders, kinderen',
    ja2eGraad = 'Ja, 2e graad: grootouders, kleinkinderen, broers, zussen',
    ja3eGraad = 'Ja, 3e graad: overgrootouders, ooms, tantes, neven, nichten',
    nee = 'Nee',
}
export const migraineInFamilieOpties = [
    MigraineInFamilie.ja1eGraad,
    MigraineInFamilie.ja2eGraad,
    MigraineInFamilie.ja3eGraad,
    MigraineInFamilie.nee,
];

export enum PreventieveMedicatie {
    amitriptyline = 'Amitriptyline',
    anders = 'Anders',
    candesartan = 'Candesartan',
    gabapentine = 'Gabapentine',
    metoprolol = 'Metoprolol',
    nortriptyline = 'Nortriptyline',
    pregabaline = 'Pregabaline',
    propranolol = 'Propranolol',
    topiramaat = 'Topiramaat',
    valproinezuur = 'Valproïnezuur',
    verapamil = 'Verapamil',
}
export const preventieveMedicatieOpties = [
    PreventieveMedicatie.candesartan,
    PreventieveMedicatie.amitriptyline,
    PreventieveMedicatie.nortriptyline,
    PreventieveMedicatie.metoprolol,
    PreventieveMedicatie.propranolol,
    PreventieveMedicatie.verapamil,
    PreventieveMedicatie.topiramaat,
    PreventieveMedicatie.valproinezuur,
    PreventieveMedicatie.gabapentine,
    PreventieveMedicatie.pregabaline,
    PreventieveMedicatie.anders,
];

export enum ZwangerschapsWens {
    jaZwanger = 'Ja, zwanger',
    jaZwangerschapsWens = 'Ja, zwangerschapswens',
    nee = 'Nee',
}
export const zwangerschapsWensOpties = [
    ZwangerschapsWens.jaZwanger,
    ZwangerschapsWens.jaZwangerschapsWens,
    ZwangerschapsWens.nee,
];

const aanvullendeOnderzoekenExtraVragen: InputField<HeadacheReferralData, CustomControlCode.HeadacheReferral>[] =
    aanvullendeOnderzoekenOpties.flatMap((aanvullendOnderzoek) => {
        const key = getObjectEntries(AanvullendeOnderzoeken).find(([, value]) => value === aanvullendOnderzoek)?.[0];
        assertIsNonNullable(key);

        const fields: InputField<HeadacheReferralData, CustomControlCode.HeadacheReferral>[] = [
            {
                include: dependsOn(
                    'aanvullende_onderzoeken',
                    ({ aanvullende_onderzoeken }) => !!aanvullende_onderzoeken[key]
                ),
                isRequired: true,
                key: `aanvullende_onderzoeken_${key}_resultaat`,
                label: `Resultaat ${aanvullendOnderzoek}`,
                type: 'radioGroup',
                valueOptions: aanvullendOnderzoekResultaatOpties,
            },
            {
                include: dependsOn(
                    `aanvullende_onderzoeken_${key}_resultaat`,
                    (data) =>
                        data[`aanvullende_onderzoeken_${key}_resultaat`] === AanvullendOnderzoekResultaat.afwijkend
                ),
                key: `aanvullende_onderzoeken_${key}_toelichting`,
                label: `Toelichting`,
                type: 'textArea',
            },
        ];

        if (key === 'bloedonderzoek') {
            fields.unshift({
                include: dependsOn(
                    'aanvullende_onderzoeken',
                    ({ aanvullende_onderzoeken }) => !!aanvullende_onderzoeken['bloedonderzoek']
                ),
                isRequired: true,
                key: `aanvullende_onderzoeken_bloedonderzoek_specificatie`,
                label: `Specificatie ${aanvullendOnderzoek}`,
                type: 'textArea',
            });
        }

        return fields;
    });

const eerdereBehandelingenExtraVragen: InputField<HeadacheReferralData, CustomControlCode.HeadacheReferral>[] =
    eerdereBehandelingenOpties.flatMap((eerdereBehandeling) => {
        const key = getObjectEntries(EerdereBehandelingen).find(([, value]) => value === eerdereBehandeling)?.[0];
        assertIsNonNullable(key);

        return [
            {
                include: dependsOn(
                    'eerdere_behandelingen',
                    ({ eerdere_behandelingen }) => !!eerdere_behandelingen[key]
                ),
                isRequired: true,
                key: `eerdere_behandelingen_${key}_resultaat`,
                label: `Resultaat ${eerdereBehandeling}`,
                type: 'radioGroup',
                valueOptions: eerdereBehandelingResultaatOpties,
            },
        ];
    });

function getMaxDosering(preventieveMedicatie: PreventieveMedicatie) {
    switch (preventieveMedicatie) {
        case PreventieveMedicatie.amitriptyline:
        case PreventieveMedicatie.nortriptyline:
            return 75;
        case PreventieveMedicatie.candesartan:
            return 50;
        case PreventieveMedicatie.gabapentine:
            return 1200;
        case PreventieveMedicatie.topiramaat:
        case PreventieveMedicatie.propranolol:
        case PreventieveMedicatie.pregabaline:
            return 200;
        case PreventieveMedicatie.metoprolol:
            return 300;
        case PreventieveMedicatie.valproinezuur:
            return 1000;
        case PreventieveMedicatie.verapamil:
            return 500;
        case PreventieveMedicatie.anders:
            return 999;
        default:
            return never(preventieveMedicatie);
    }
}

const geprobeerdePreventieveMedicatieExtraVragen: InputField<
    HeadacheReferralData,
    CustomControlCode.HeadacheReferral
>[] = preventieveMedicatieOpties.flatMap((preventieveMedicatie) => {
    const key = getObjectEntries(PreventieveMedicatie).find(([, value]) => value === preventieveMedicatie)?.[0];
    assertIsNonNullable(key);

    const include: DependsOn<HeadacheReferralData, boolean, CustomControlCode.HeadacheReferral> = dependsOn(
        'preventieve_medicatie',
        ({ preventieve_medicatie }) => Boolean(preventieve_medicatie[key])
    );

    return [
        {
            append: 'mg per keer',
            include,
            isRequired: true,
            key: `preventieve_medicatie_${key}_dosering_per_keer`,
            label: `Definitieve dosering van ${preventieveMedicatie}`,
            maxValue: getMaxDosering(preventieveMedicatie),
            minValue: 1,
            type: 'number',
        },
        {
            append: 'keer per dag',
            include,
            isRequired: true,
            key: `preventieve_medicatie_${key}_dosering_per_dag`,
            label: ``,
            maxValue: 4,
            minValue: 1,
            type: 'number',
        },
        {
            include,
            isRequired: true,
            key: `preventieve_medicatie_${key}_momenteel_gebruikt`,
            label: `De patiënt gebruikt op dit moment ${preventieveMedicatie}`,
            type: 'boolean',
        },
    ];
});

export type HeadacheReferralData = NullablePrimitives<{
    aanvullende_onderzoeken: NullablePrimitives<{
        anders: string;
        bloedonderzoek: boolean;
        ct_scan: boolean;
        eeg: boolean;
        mri: boolean;
        oogonderzoek: boolean;
    }>;
    aanvullende_onderzoeken_anders_resultaat: AanvullendOnderzoekResultaat;
    aanvullende_onderzoeken_anders_toelichting: string;
    aanvullende_onderzoeken_bloedonderzoek_resultaat: AanvullendOnderzoekResultaat;
    aanvullende_onderzoeken_bloedonderzoek_specificatie: string;
    aanvullende_onderzoeken_bloedonderzoek_toelichting: string;
    aanvullende_onderzoeken_ct_scan_resultaat: AanvullendOnderzoekResultaat;
    aanvullende_onderzoeken_ct_scan_toelichting: string;
    aanvullende_onderzoeken_eeg_resultaat: AanvullendOnderzoekResultaat;
    aanvullende_onderzoeken_eeg_toelichting: string;
    aanvullende_onderzoeken_gedaan: boolean;
    aanvullende_onderzoeken_mri_resultaat: AanvullendOnderzoekResultaat;
    aanvullende_onderzoeken_mri_toelichting: string;
    aanvullende_onderzoeken_oogonderzoek_resultaat: AanvullendOnderzoekResultaat;
    aanvullende_onderzoeken_oogonderzoek_toelichting: string;
    baan_met_onregelmatige_werkuren: boolean;
    comorbiditeit: string;
    eerdere_behandelingen: NullablePrimitives<{
        anders: string;
        ergotherapie: boolean;
        fysio_manuele_therapie: boolean;
        oefentherapie: boolean;
        psychologische_behandeling: boolean;
        voedingsaanpassingen_door_dietist: boolean;
    }>;
    eerdere_behandelingen_anders_resultaat: string;
    eerdere_behandelingen_ergotherapie_resultaat: string;
    eerdere_behandelingen_fysio_manuele_therapie_resultaat: string;
    eerdere_behandelingen_oefentherapie_resultaat: string;
    eerdere_behandelingen_ondergaan: boolean;
    eerdere_behandelingen_psychologische_behandeling_resultaat: string;
    eerdere_behandelingen_voedingsaanpassingen_door_dietist_resultaat: string;
    exclusiecriteria_gelezen: boolean;
    gebruik_anticonceptiepil: boolean;
    hoofdpijn_begeleidende_symptomen: boolean;
    hoofdpijn_klachten_menstruatie_van_invloed: HoofdpijnKlachtenMenstruatieVanInvloed;
    hoofdpijn_locatie: Locatie;
    hoofdpijn_start_datum: string;
    hoofdpijn_symptomen: NullablePrimitives<{
        anders: string;
        auras: boolean;
        gevoelig_voor_licht_en_geluid: boolean;
        misselijkheid_en_of_braken: boolean;
        tintelingen_of_andere: boolean;
    }>;
    huidige_medicatie_niet_hoofdpijn: string;
    hulpvraag_patient: string;
    migraine_of_hoofdpijn_in_familie: MigraineInFamilie;
    patient: Pick<IPatientAddEditValidated, 'emailAddress' | 'homePhoneNumber' | 'mobileNumber'>;
    preventieve_medicatie: NullablePrimitives<{
        amitriptyline: boolean;
        anders: string;
        candesartan: boolean;
        gabapentine: boolean;
        metoprolol: boolean;
        nortriptyline: boolean;
        pregabaline: boolean;
        propranolol: boolean;
        topiramaat: boolean;
        valproinezuur: boolean;
        verapamil: boolean;
    }>;
    preventieve_medicatie_amitriptyline_dosering_per_dag: number;
    preventieve_medicatie_amitriptyline_dosering_per_keer: number;
    preventieve_medicatie_amitriptyline_momenteel_gebruikt: boolean;
    preventieve_medicatie_anders_dosering_per_dag: number;
    preventieve_medicatie_anders_dosering_per_keer: number;
    preventieve_medicatie_anders_momenteel_gebruikt: boolean;
    preventieve_medicatie_candesartan_dosering_per_dag: number;
    preventieve_medicatie_candesartan_dosering_per_keer: number;
    preventieve_medicatie_candesartan_momenteel_gebruikt: boolean;
    preventieve_medicatie_gabapentine_dosering_per_dag: number;
    preventieve_medicatie_gabapentine_dosering_per_keer: number;
    preventieve_medicatie_gabapentine_momenteel_gebruikt: boolean;
    preventieve_medicatie_geprobeerd: boolean;
    preventieve_medicatie_metoprolol_dosering_per_dag: number;
    preventieve_medicatie_metoprolol_dosering_per_keer: number;
    preventieve_medicatie_metoprolol_momenteel_gebruikt: boolean;
    preventieve_medicatie_nortriptyline_dosering_per_dag: number;
    preventieve_medicatie_nortriptyline_dosering_per_keer: number;
    preventieve_medicatie_nortriptyline_momenteel_gebruikt: boolean;
    preventieve_medicatie_pregabaline_dosering_per_dag: number;
    preventieve_medicatie_pregabaline_dosering_per_keer: number;
    preventieve_medicatie_pregabaline_momenteel_gebruikt: boolean;
    preventieve_medicatie_propranolol_dosering_per_dag: number;
    preventieve_medicatie_propranolol_dosering_per_keer: number;
    preventieve_medicatie_propranolol_momenteel_gebruikt: boolean;
    preventieve_medicatie_topiramaat_dosering_per_dag: number;
    preventieve_medicatie_topiramaat_dosering_per_keer: number;
    preventieve_medicatie_topiramaat_momenteel_gebruikt: boolean;
    preventieve_medicatie_valproinezuur_dosering_per_dag: number;
    preventieve_medicatie_valproinezuur_dosering_per_keer: number;
    preventieve_medicatie_valproinezuur_momenteel_gebruikt: boolean;
    preventieve_medicatie_verapamil_dosering_per_dag: number;
    preventieve_medicatie_verapamil_dosering_per_keer: number;
    preventieve_medicatie_verapamil_momenteel_gebruikt: boolean;
    thuissituatie: string;
    vermoedelijke_diagnose_extra_opmerkingen: string;
    vermoedelijke_diagnose_hoofdpijn_type: HoofdpijnType;
    vraag_aan_neuroloog: string;
    zwangerschap_of_zwangerschapswens: ZwangerschapsWens;
}> &
    Pick<GenericData, 'patient_verzekeringskosten_akkoord'>;

export type HeadacheReferralKey = DeepPickPath<HeadacheReferralData>;

export const headacheReferralSections: Section<HeadacheReferralData, undefined, CustomControlCode.HeadacheReferral>[] =
    [
        {
            fields: [
                patient_verzekeringskosten_akkoord_field,
                {
                    draftSchema: Koi.boolean().allow(null),
                    finalSchema: Koi.boolean().valid(true),
                    initialValue: null,
                    isRequired: true,
                    key: 'exclusiecriteria_gelezen',
                    label: 'Ik heb de exclusiecriteria gelezen en ga akkoord.',
                    type: 'custom',
                },
            ],
        },
        {
            fields: [
                {
                    draftSchema: getTextInputStringSchema(true, 255),
                    finalSchema: emailSchema,
                    isRequired: true,
                    key: 'patient.emailAddress',
                    label: 'E-mailadres',
                    type: 'textInput',
                },
                {
                    isRequired: true,
                    key: 'patient.mobileNumber',
                    label: 'Telefoonnummer',
                    type: 'textInput',
                },
                {
                    key: 'patient.homePhoneNumber',
                    label: 'Thuisnummer',
                    type: 'textInput',
                },
            ],
            title: 'Gegevens patiënt',
        },
        {
            fields: [
                {
                    allowOtherOption: true,
                    isRequired: true,
                    key: 'thuissituatie',
                    label: 'Thuissituatie',
                    type: 'radioGroup',
                    valueOptions: thuissituatieOptions,
                },
                {
                    isRequired: true,
                    key: 'baan_met_onregelmatige_werkuren',
                    label: 'Baan met onregelmatige werkuren en/of nachtdiensten',
                    type: 'boolean',
                },
                {
                    isRequired: true,
                    key: 'migraine_of_hoofdpijn_in_familie',
                    label: 'Migraine en/of hoofdpijn bekend in familie',
                    type: 'radioGroup',
                    valueOptions: migraineInFamilieOpties,
                },
                {
                    isRequired: true,
                    key: 'huidige_medicatie_niet_hoofdpijn',
                    label: 'Huidige medicatie (anders dan medicatie tegen hoofdpijn)',
                    type: 'textArea',
                },
                {
                    isRequired: true,
                    key: 'comorbiditeit',
                    label: 'Comorbiditeit',
                    type: 'textInput',
                },
                {
                    include: dependsOn('patientData.gender', ({ patientData }) => patientData.gender !== Gender.male),
                    isRequired: true,
                    key: 'zwangerschap_of_zwangerschapswens',
                    label: 'Zwangerschap of zwangerschapswens op korte termijn',
                    type: 'radioGroup',
                    valueOptions: zwangerschapsWensOpties,
                },
                {
                    include: dependsOn('patientData.gender', ({ patientData }) => patientData.gender !== Gender.male),
                    isRequired: true,
                    key: 'gebruik_anticonceptiepil',
                    label: 'Gebruikt anticonceptiepil',
                    type: 'boolean',
                },
            ],
            subsections: [
                {
                    fields: [
                        {
                            isRequired: true,
                            key: 'hulpvraag_patient',
                            label: 'Hulpvraag van de patiënt',
                            type: 'textInput',
                        },
                        {
                            draftSchema: Koi.koi().date().allow(null),
                            finalSchema: getTodayOrBeforeDateSchema(),
                            isRequired: true,
                            key: 'hoofdpijn_start_datum',
                            label: 'Datum start van de hoofdpijn',
                            maxValue: new Date(),
                            type: 'monthInput',
                        },
                        {
                            isRequired: true,
                            key: 'hoofdpijn_locatie',
                            label: 'Locatie',
                            type: 'radioGroup',
                            valueOptions: locatieOptions,
                        },
                        {
                            isRequired: true,
                            key: 'hoofdpijn_begeleidende_symptomen',
                            label: 'Begeleidende symptomen',
                            type: 'boolean',
                        },
                        {
                            checkboxEnumType: HoofdpijnSymptomen,
                            include: dependsOn(`hoofdpijn_begeleidende_symptomen`),
                            isRequired: true,
                            key: 'hoofdpijn_symptomen',
                            label: 'Symptomen',
                            type: 'checkboxGroup',
                            valueOptions: hoofdpijnSymptomenOpties,
                        },
                        {
                            include: dependsOn(
                                'patientData.gender',
                                ({ patientData }) => patientData.gender !== Gender.male
                            ),
                            isRequired: true,
                            key: 'hoofdpijn_klachten_menstruatie_van_invloed',
                            label: 'Invloed menstruatie op klachten',
                            type: 'radioGroup',
                            valueOptions: hoofdpijnKlachtenMenstruatieVanInvloedOpties,
                        },
                    ],
                    title: 'Huidige klachten',
                },
                {
                    fields: [
                        {
                            isRequired: true,
                            key: 'preventieve_medicatie_geprobeerd',
                            label: 'Preventieve medicatie geprobeerd',
                            type: 'boolean',
                        },
                        {
                            checkboxEnumType: PreventieveMedicatie,
                            include: dependsOn(`preventieve_medicatie_geprobeerd`),
                            isRequired: true,
                            key: 'preventieve_medicatie',
                            label: 'Geprobeerde preventieve medicatie',
                            type: 'checkboxGroup',
                            valueOptions: preventieveMedicatieOpties,
                        },
                        ...geprobeerdePreventieveMedicatieExtraVragen,
                        {
                            isRequired: true,
                            key: 'aanvullende_onderzoeken_gedaan',
                            label: 'Aanvullend onderzoek gedaan',
                            type: 'boolean',
                        },
                        {
                            checkboxEnumType: AanvullendeOnderzoeken,
                            include: dependsOn(`aanvullende_onderzoeken_gedaan`),
                            isRequired: true,
                            key: 'aanvullende_onderzoeken',
                            label: 'Aanvullende onderzoeken',
                            type: 'checkboxGroup',
                            valueOptions: aanvullendeOnderzoekenOpties,
                        },
                        ...aanvullendeOnderzoekenExtraVragen,
                        {
                            isRequired: true,
                            key: 'eerdere_behandelingen_ondergaan',
                            label: 'Eerdere behandelingen ondergaan',
                            type: 'boolean',
                        },
                        {
                            checkboxEnumType: EerdereBehandelingen,
                            include: dependsOn(`eerdere_behandelingen_ondergaan`),
                            isRequired: true,
                            key: 'eerdere_behandelingen',
                            label: 'Eerdere behandelingen',
                            type: 'checkboxGroup',
                            valueOptions: eerdereBehandelingenOpties,
                        },
                        ...eerdereBehandelingenExtraVragen,
                    ],
                    title: 'Behandelgeschiedenis',
                },
            ],
            title: 'Anamnese',
        },
        {
            fields: [
                {
                    allowOtherOption: true,
                    isRequired: true,
                    key: 'vermoedelijke_diagnose_hoofdpijn_type',
                    label: 'Type hoofdpijn',
                    type: 'radioGroup',
                    valueOptions: hoofdpijnTypeOpties,
                },
                {
                    key: 'vermoedelijke_diagnose_extra_opmerkingen',
                    label: 'Extra opmerkingen',
                    type: 'textArea',
                },
            ],
            title: 'Vermoedelijke diagnose',
        },
        {
            fields: [
                {
                    key: 'vraag_aan_neuroloog',
                    label: 'Vraag aan neuroloog',
                    type: 'textArea',
                },
            ],
            title: 'Overige vragen',
        },
    ];
